import { useCallback, useState, type ReactNode } from 'react'
import { Button } from '@orisini/ui/components/button'

/** Pilihan jumlah baris per halaman; yang pertama dipakai sebagai default. */
export const PAGE_SIZES = [20, 30, 50, 100] as const

/**
 * Pagination sisi klien untuk daftar yang sudah utuh di memori.
 *
 * Halaman dijepit saat render, bukan lewat efek: daftar bisa menyusut kapan
 * saja (pin dibatalkan, terbit sendiri) dan slice tidak boleh sempat kosong.
 */
export function usePagination<T>(
  items: T[],
  satuan = 'rows',
): { visible: T[]; pager: ReactNode; resetPage: () => void } {
  const [perPage, setPerPage] = useState<number>(PAGE_SIZES[0])
  const [page, setPage] = useState(1)

  const totalPages = Math.max(1, Math.ceil(items.length / perPage))
  const currentPage = Math.min(page, totalPages)
  const firstIndex = (currentPage - 1) * perPage
  const visible = items.slice(firstIndex, firstIndex + perPage)

  const resetPage = useCallback(() => setPage(1), [])

  const pager =
    items.length > PAGE_SIZES[0] ? (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {firstIndex + 1}–{firstIndex + visible.length} of {items.length} {satuan}
        </p>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            Show
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value))
                setPage(1)
              }}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setPage(currentPage - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setPage(currentPage + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    ) : null

  return { visible, pager, resetPage }
}
